import { createSlice } from "@reduxjs/toolkit";

import {
    deleteLandingPage,
    getCloneLandingPage,
    getGamificationTemplates,
    getGenerateCode,
    getIOSAppDetails,
    getLandingPages,
    getLandingPageTemplates,
    getLeadCustomSources,
    getMMPDetails,
    getPublishLandingPage,
    postCreateLandingPage,
    putUpdateLandingPage,
} from "./landingPages.asyncActions";
import { initialState } from "./landingPages.types";

export const landingPagesSlice = createSlice({
    name: "landingPages",
    initialState,
    reducers: {
        clearState: (state, action) => {
            return {
                ...initialState,
                lastAddedLandingPage: state.lastAddedLandingPage,
                landingPageDetails: action?.payload?.clearDetails
                    ? null
                    : state.landingPageDetails,
            };
        },
        setLandingPageDetails: (state, action) => {
            state.landingPageDetails = action.payload;
        },
        clearLastAddedLandingPage: (state) => {
            state.lastAddedLandingPage = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getLandingPages.pending, (state) => {
                state.landingPages.isLoadingList = true;
            })
            .addCase(getLandingPages.fulfilled, (state, action) => {
                state.landingPages.data = action.payload.pages;
                state.landingPages.pagination = action.payload.pagination;
                state.landingPages.isLoadingList = false;
            })
            .addCase(getLandingPages.rejected, (state) => {
                state.landingPages.isLoadingList = false;
            });

        builder
            .addCase(getLandingPageTemplates.pending, (state) => {
                state.templates.isLoadingList = true;
            })
            .addCase(getLandingPageTemplates.fulfilled, (state, action) => {
                state.templates.data = action.payload;
                state.templates.isLoadingList = false;
            })
            .addCase(getLandingPageTemplates.rejected, (state) => {
                state.templates.isLoadingList = false;
            });

        builder
            .addCase(getGenerateCode.pending, (state) => {
                state.code.isLoading = true;
            })
            .addCase(getGenerateCode.fulfilled, (state, action) => {
                state.code.data = action.payload;
                state.code.isLoading = false;
            })
            .addCase(getGenerateCode.rejected, (state) => {
                state.code.isLoading = false;
            });

        builder
            .addCase(getCloneLandingPage.pending, (state) => {
                state.isCloneLandingPageLoading = true;
            })
            .addCase(getCloneLandingPage.fulfilled, (state, action) => {
                state.code.data = action.payload;
                state.isCloneLandingPageLoading = false;
            })
            .addCase(getCloneLandingPage.rejected, (state) => {
                state.isCloneLandingPageLoading = false;
            });

        builder
            .addCase(postCreateLandingPage.pending, (state) => {
                state.isLandingPageFormLoading = true;
                state.lastAddedDraftLandingPage = null;
            })
            .addCase(postCreateLandingPage.fulfilled, (state, action) => {
                state.isLandingPageFormLoading = false;
                state.lastAddedDraftLandingPage = action.payload;
            })
            .addCase(postCreateLandingPage.rejected, (state) => {
                state.isLandingPageFormLoading = false;
            });
        builder
            .addCase(getPublishLandingPage.pending, (state) => {
                state.isLandingPageFormLoading = true;
                state.lastAddedLandingPage = null;
            })
            .addCase(getPublishLandingPage.fulfilled, (state, action) => {
                state.isLandingPageFormLoading = false;
                state.lastAddedLandingPage = action.meta?.arg?.id;
                state.isLandingPageCreated = true;
            })
            .addCase(getPublishLandingPage.rejected, (state) => {
                state.isLandingPageFormLoading = false;
            });
        builder
            .addCase(getGamificationTemplates.pending, (state) => {
                state.gamificationTemplates.isLoadingList = true;
            })
            .addCase(getGamificationTemplates.fulfilled, (state, action) => {
                state.gamificationTemplates.data = action.payload;
                state.gamificationTemplates.isLoadingList = false;
            })
            .addCase(getGamificationTemplates.rejected, (state) => {
                state.gamificationTemplates.isLoadingList = false;
            });

        builder
            .addCase(putUpdateLandingPage.pending, (state) => {
                state.isLandingPageFormLoading = true;
            })
            .addCase(putUpdateLandingPage.fulfilled, (state, action) => {
                state.isLandingPageCreated =
                    state.lastAddedDraftLandingPage ||
                    !state.landingPageDetails?.is_published
                        ? false
                        : true;
                state.isLandingPageFormLoading = false;
            })
            .addCase(putUpdateLandingPage.rejected, (state) => {
                state.isLandingPageFormLoading = false;
            });

        builder
            .addCase(getIOSAppDetails.pending, (state) => {
                state.appDetails.isLoading = true;
            })
            .addCase(getIOSAppDetails.fulfilled, (state, action) => {
                state.appDetails.data = action.payload;
                state.appDetails.isLoading = false;
            })
            .addCase(getIOSAppDetails.rejected, (state) => {
                state.appDetails.isLoading = false;
            });

        builder
            .addCase(getMMPDetails.pending, (state) => {
                state.mmpDetails.isLoading = true;
            })
            .addCase(getMMPDetails.fulfilled, (state, action) => {
                state.mmpDetails.data = action.payload?.data?.app_ids;
                state.mmpDetails.isLoading = false;
            })
            .addCase(getMMPDetails.rejected, (state) => {
                state.mmpDetails.isLoading = false;
            });

        builder
            .addCase(getLeadCustomSources.pending, (state) => {
                state.customSources.isLoadingList = true;
            })
            .addCase(getLeadCustomSources.fulfilled, (state, action) => {
                state.customSources.data = action.payload;
                state.customSources.isLoadingList = false;
            })
            .addCase(getLeadCustomSources.rejected, (state) => {
                state.customSources.isLoadingList = false;
            });

        builder
            .addCase(deleteLandingPage.pending, (state, action) => {
                state.deletingLandingPageId = action.meta.arg;
            })
            .addCase(deleteLandingPage.fulfilled, (state) => {
                state.deletingLandingPageId = null;
            })
            .addCase(deleteLandingPage.rejected, (state) => {
                state.deletingLandingPageId = null;
            });
    },
});

export default landingPagesSlice.reducer;
export const { clearState, setLandingPageDetails, clearLastAddedLandingPage } =
    landingPagesSlice.actions;
